/**
 * Pure helpers behind the dictated-day card. Kept out of the component, same
 * convention as stewardCard-utils.ts, so the load-bearing contract — how a
 * proposed stop becomes a real itinerary row — is testable without a DOM.
 *
 * The one rule everything here protects: ORDER IS THE TRAVELER'S. Nothing in
 * this file sorts. A day arrives as a sequence someone dictated out loud, and
 * re-ordering it by clock time would silently rewrite what they said.
 */
import type {
  ItineraryItemInput,
  StewardBlockRef,
  StewardCardData,
  StewardDayPlanData,
  StewardDayStop,
  StewardMessage,
} from "@/lib/types";

const WEEKDAY = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Map one dictated stop onto the body of the EXISTING
 * POST /trips/:id/itinerary-items — the same write path as the manual add
 * dialog and StewardCard.
 *
 * ⚠️ `day_date` COMES FROM THE PLAN, NEVER FROM THE STOP. A stop carries no
 * date at all: the whole point of a dictated day is that it is one day, and
 * letting a stop carry its own date would let a model scatter twelve rows
 * across the trip from a single tap.
 *
 * `is_anchor` is never set — Steward must not reassign the trip's anchor — and
 * no `cost` is sent, because a dictated day asserts no price.
 */
export function stopToItemInput(
  stop: StewardDayStop,
  dayDate: string,
): ItineraryItemInput {
  return {
    item_type: stop.item_type,
    title: stop.title,
    city: stop.city ?? null,
    location: stop.location ?? null,
    day_date: dayDate,
    start_time: stop.start_time ?? null,
    end_time: stop.end_time ?? null,
    notes: stop.notes ?? null,
  };
}

/** "Saturday 12 September" — the day, spelled out. Falls back to the raw value. */
export function formatDayDate(dayDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayDate)) return dayDate;
  const [y, m, d] = dayDate.split("-").map(Number);
  if (!y || !m || !d || m > 12) return dayDate;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return dayDate;
  return `${WEEKDAY[dt.getUTCDay()]} ${d} ${MONTH[m - 1]}`;
}

/** The card's headline: what Steward called the day, or the date spelled out. */
export function dayHeadline(day: StewardDayPlanData): string {
  return day.label?.trim() || formatDayDate(day.day_date);
}

/**
 * The short name the Add button uses ("Add 12 stops to Sat 12 Sep"). Always the
 * DATE, never the label: a label can be anything ("Our big drive day") and the
 * button has to say which day the rows land on.
 */
export function dayButtonDay(day: StewardDayPlanData): string {
  const full = formatDayDate(day.day_date);
  if (full === day.day_date) return day.day_date;
  const [weekday, dom, month] = full.split(" ");
  return `${weekday.slice(0, 3)} ${dom} ${month.slice(0, 3)}`;
}

/**
 * "Add 12 stops to Sat 12 Sep" / "Add 1 stop to Sat 12 Sep".
 *
 * The count is always spoken because the traveler can untick rows, and a button
 * that says "Add" while three stops are unticked is a button that lies about
 * what it is about to do.
 */
export function addButtonLabel(
  day: StewardDayPlanData,
  count: number,
  partiallyAdded = false,
): string {
  const stops = `${count} stop${count === 1 ? "" : "s"}`;
  // "Add 1 more" once some of the day is already on the itinerary — "Add 1 stop
  // to Sat 12 Sep" would read as though nothing had landed yet.
  return partiallyAdded
    ? `Add ${count} more to ${dayButtonDay(day)}`
    : `Add ${stops} to ${dayButtonDay(day)}`;
}

/**
 * The subhead. Says the posture out loud, because a list of rows that looks
 * like an itinerary IS an itinerary as far as the reader is concerned, and the
 * whole contract is that nothing has been written.
 */
export function dayPosture(day: StewardDayPlanData): string {
  const n = day.stops.length;
  return `${n} stop${n === 1 ? "" : "s"}, in the order you gave them. Nothing is saved until you add it.`;
}

/** "8:00 AM – 9:30 AM", "8:00 AM", or an em-dash when no time was given. */
export function stopTimeLabel(stop: StewardDayStop): string {
  const fmt = (t: string | null | undefined): string | null => {
    if (!t) return null;
    const m = /^(\d{1,2}):(\d{2})/.exec(t.trim());
    if (!m) return t;
    let h = Number(m[1]);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${m[2]} ${ampm}`;
  };
  const start = fmt(stop.start_time);
  const end = fmt(stop.end_time);
  if (start && end) return `${start} – ${end}`;
  // An end with no start is still information ("until 6pm") and is kept.
  return start ?? end ?? "—";
}

/**
 * The stops the traveler has left ticked, in the plan's order.
 *
 * Selection is by INDEX, not by title: a dictated day can legitimately repeat a
 * title ("Coffee" twice), and keying on the text would drop both when one is
 * unticked.
 */
export function selectedStops(
  day: StewardDayPlanData,
  selected: readonly number[],
): { index: number; stop: StewardDayStop }[] {
  const keep = new Set(selected);
  return day.stops
    .map((stop, index) => ({ index, stop }))
    .filter(({ index }) => keep.has(index));
}

/** Every index in the plan — the default, because the whole day is the proposal. */
export function allIndexes(day: StewardDayPlanData): number[] {
  return day.stops.map((_, i) => i);
}

export interface AddOutcome {
  /** Indexes that reached the itinerary. */
  added: number[];
  /** Indexes the API refused. Still ticked, still retryable. */
  failed: number[];
}

/**
 * The sentence shown after an add attempt.
 *
 * HONEST ABOUT PARTIALS. If 9 of 12 landed, it says exactly that — a serial
 * loop over createItem can genuinely half-succeed, and reporting the whole day
 * as added would leave the traveler believing in three rows that do not exist.
 * Returns null when everything worked; the button's "Added" state says that.
 */
export function addOutcomeMessage(outcome: AddOutcome): string | null {
  const ok = outcome.added.length;
  const bad = outcome.failed.length;
  if (bad === 0) return null;
  if (ok === 0) {
    return bad === 1
      ? "Couldn't add that stop — it's still ticked, try again."
      : `Couldn't add ${bad} stops — they're still ticked, try again.`;
  }
  return `Added ${ok} of ${ok + bad} stops. The other ${bad} didn't go through — ${
    bad === 1 ? "it's" : "they're"
  } still ticked, try again.`;
}

export type StewardBlock =
  | { kind: "card"; key: string; card: StewardCardData }
  | { kind: "day"; key: string; day: StewardDayPlanData };

/**
 * A turn's proposals in the order it emitted them.
 *
 * A live turn records `blocks` as the SSE frames arrive, so a day that preceded
 * a card renders above it. A rehydrated turn has no `blocks` — the stored
 * actions blob keeps cards and days as separate arrays and cannot say which
 * came first — so it falls back to days-then-cards: a dictated day is the frame
 * the cards sit inside, so it reads correctly either way.
 *
 * Anything `blocks` references but the arrays no longer hold is skipped, and
 * anything the arrays hold but `blocks` missed is appended, so a malformed
 * ordering can never make a proposal disappear.
 */
export function orderedBlocks(message: {
  cards: StewardCardData[];
  days: StewardDayPlanData[];
  blocks?: StewardBlockRef[];
}): StewardBlock[] {
  const days = message.days ?? [];
  const cards = message.cards ?? [];
  const out: StewardBlock[] = [];
  const seen = new Set<string>();

  const push = (b: StewardBlock) => {
    const id = `${b.kind}:${b.key}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push(b);
  };

  for (const ref of message.blocks ?? []) {
    if (ref.kind === "day") {
      const day = days.find((d) => d.day_date === ref.key);
      if (day) push({ kind: "day", key: day.day_date, day });
    } else {
      const card = cards.find((c) => c.card_key === ref.key);
      if (card) push({ kind: "card", key: card.card_key, card });
    }
  }

  for (const day of days) push({ kind: "day", key: day.day_date, day });
  for (const card of cards) push({ kind: "card", key: card.card_key, card });

  return out;
}

/** Convenience for the workspace, which holds whole StewardMessages. */
export function messageBlocks(message: StewardMessage): StewardBlock[] {
  return orderedBlocks(message);
}
